import { AxiosError } from 'axios';
import pino from 'pino';
import type { CSSProperties } from 'react';
import { isRouteErrorResponse, Navigate, useLocation, useParams, useRouteError } from 'react-router';

import { errorStrings } from '../constants/strings';
import { SessionRepository } from '../repositories/sessionRepository';

import { savePreLoginRouterState } from './routerUtils';

const logger = pino();

const styles: Record<string, CSSProperties> = {
	container: {
		width: '100%',
		height: '100%',
		backgroundColor: 'hsl(325, 98%, 33%) ',
		color: '#FFFFFF',
		position: 'fixed',
		top: 0,
		left: 0,
	},
	errorBox: {
		position: 'absolute',
		left: '50%',
		top: '50%',
		transform: 'translate(-50%, -50%)',
		textAlign: 'center',
	},
	button: {
		padding: '10px 20px',
		backgroundColor: '#FFFFFF',
		color: '#FF69B4',
		border: 'none',
		borderRadius: '4px',
		cursor: 'pointer',
		marginTop: '20px',
		fontSize: '16px',
	},
};

export const errorStatusMessages = {
	404: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
		? errorStrings.HSBC_PAGE_NOT_FOUND_HEADER
		: 'This page does not exist!',
	401: 'You are not authorized to see this!',
	403: 'You do not have permission to access this resource!',
	500: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? errorStrings.DEFAULT : 'Looks like our API is down!',
};

const goBack = () => {
	void SessionRepository.logout();
	savePreLoginRouterState({
		redirectTo: globalThis.location.pathname,
		redirectParams: globalThis.location.search,
	});
	globalThis.location.href = '/signin/login';
};

const ReactRouterErrorBoundary = () => {
	const error = useRouteError();
	const location = useLocation();
	const params = useParams();

	logger.error({
		error: error instanceof Error ? error.message : 'Unknown error',
		stack: error instanceof Error ? error.stack : undefined,
	});

	// Axios errors
	if (error instanceof AxiosError) {
		if (error.response?.status === 403) {
			const isViewConversationRoute =
				(location.pathname.includes('activity') || location.pathname.includes('approvals')) && params['approvalId'];
			const isViewWalletCardRoute = location.pathname.includes('wallet') && params['taskId'];
			const isViewRequestConversationRoute = location.pathname.includes('requests') && params['requestId'];

			if (isViewConversationRoute || isViewWalletCardRoute || isViewRequestConversationRoute) {
				return <Navigate to="/signin/login" replace />;
			}

			const errorMessage = 'You do not have permission to access this resource!';
			return (
				<div style={styles['container']}>
					<div style={styles['errorBox']}>
						<h1>{errorMessage}</h1>
						<p>{error.message}</p>
						<button style={styles['button']} onClick={goBack}>
							{'Go back to login'}
						</button>
					</div>
				</div>
			);
		}
		if (error.response?.status === 401) {
			logger.info('401 error detected, redirecting to login');
			void SessionRepository.logout();
			return <Navigate to="/signin/login" replace />;
		}
	}

	// React Router errors
	if (isRouteErrorResponse(error) && (error.status === 403 || error.status === 401)) {
		logger.info('Router 403/401 error detected, redirecting to login');
		void SessionRepository.logout();
		return <Navigate to="/signin/login" replace />;
	}

	const errorMessage = isRouteErrorResponse(error)
		? errorStatusMessages[error.status] || 'Sorry, we are having technical difficulties. Please try again later.'
		: 'Sorry, we are having technical difficulties. Please try again later.';

	return (
		<div style={styles['container']}>
			<div style={styles['errorBox']}>
				<h1>{errorMessage}</h1>
				<p>{error instanceof Error ? error.message : String(error)}</p>
				<button style={styles['button']} onClick={goBack}>
					{'Go back'}
				</button>
			</div>
		</div>
	);
};

export default ReactRouterErrorBoundary;
